/**
 * Нумерация партий плана готовой продукции общей смены.
 * Чистая функция — вызывается из ShiftCustomPlanForm.onEquipmentChange,
 * покрыта unit-тестами (tests/unit/batch-number.spec.ts).
 */

/**
 * Номер партии новой записи плана ГП.
 * С оборудованием: «Префикс + буква времени суток + -NN» (КД-01, А160Н-01),
 * где NN — максимальный существующий NN на этом оборудовании + 1 (по хвостовым
 * цифрам номеров планов смены с этим оборудованием; при ручных номерах без
 * цифр — fallback на количество таких планов + 1), двузначное
 * (10-я партия — «-10»). Продолжение от максимума, а не от количества —
 * нумерация не ломается дублями/пропусками.
 * Без оборудования (или без префикса): прежний формат — порядковый номер
 * по смене + первая буква времени суток (1Д, 2Н).
 */
export function buildEquipmentBatchNumber(
  equipment: { id: any; prefix?: string | null } | null | undefined,
  relatedPlansList: any[],
  relTimeOfDay: string | null | undefined,
): string {
  const letter = relTimeOfDay ? relTimeOfDay[0] : '';

  if (!equipment || !equipment.prefix) {
    return (relatedPlansList.length + 1) + letter;
  }

  const sameEquipmentPlans = relatedPlansList.filter((plan: any) => {
    return plan.equipment_id == equipment.id;
  });

  const existingSeqs = sameEquipmentPlans
    .map((plan: any) => {
      const digits = String(plan.number || '').match(/(\d+)$/);
      return digits ? parseInt(digits[1], 10) : NaN;
    })
    .filter((seq: number) => !isNaN(seq));

  const seq = (existingSeqs.length ? Math.max.apply(null, existingSeqs) : sameEquipmentPlans.length) + 1;

  return `${equipment.prefix}${letter.toUpperCase()}-${String(seq).padStart(2, '0')}`;
}

/**
 * Следующий номер партии при создании нескольких партий одной кнопкой
 * (поле «Количество партий» в форме плана ГП). Наращивается порядковая
 * часть номера из формы, оба формата номера:
 * - хвостовые цифры «КД-01» → «КД-02» (ширина нулей сохраняется);
 * - иначе ведущие цифры «1Д» → «2Д».
 * Номер без цифр (вручную введённый экзотический) не трогаем.
 */
export function incrementBatchNumber(number: string): string {
  const trailing = number.match(/(\d+)$/);
  if (trailing) {
    const next = String(parseInt(trailing[1], 10) + 1).padStart(trailing[1].length, '0');
    return number.slice(0, trailing.index) + next;
  }

  const leading = number.match(/^(\d+)/);
  if (leading) {
    return String(parseInt(leading[1], 10) + 1) + number.slice(leading[1].length);
  }

  return number;
}

/**
 * Номера для N создаваемых партий: базовый номер из формы + наращивание
 * для каждой следующей («КД-01», «КД-02», «КД-03»).
 */
export function buildBatchNumbers(base: string, count: number): string[] {
  const total = Math.max(1, Math.floor(count));
  const numbers = [base];

  for (let idx = 1; idx < total; idx++) {
    numbers.push(incrementBatchNumber(numbers[idx - 1]));
  }

  return numbers;
}
